import { VIDEO_GENERATION } from './constants';

export interface SceneTimingInput {
  audioDuration?: number;
  additionalDisplayTimeBefore?: number;
  additionalDisplayTimeAfter?: number;
}

export interface SceneDuration {
  baseTime: number;
  beforeTime: number;
  afterTime: number;
  total: number;
}

/**
 * シーンの表示時間を計算
 * 最初のシーンはbeforeTimeを0にする（動画開始時は余韻時間不要）
 */
export function calculateSceneDuration(
  scene: SceneTimingInput,
  isFirstScene: boolean
): SceneDuration {
  const baseTime = scene.audioDuration || VIDEO_GENERATION.DEFAULT_DURATION_NO_AUDIO;
  const beforeTime = isFirstScene ? 0 : (scene.additionalDisplayTimeBefore ?? 0);
  const afterTime = scene.additionalDisplayTimeAfter ?? 0;
  return {
    baseTime,
    beforeTime,
    afterTime,
    total: beforeTime + baseTime + afterTime,
  };
}

/**
 * 全シーンの表示時間と動画全体の長さを計算
 */
export function buildSceneDurations(scenes: SceneTimingInput[]): {
  durations: SceneDuration[];
  totalDuration: number;
} {
  const durations = scenes.map((scene, index) => calculateSceneDuration(scene, index === 0));
  const totalDuration = durations.reduce((sum, d) => sum + d.total, 0);
  return { durations, totalDuration };
}

/**
 * 出力解像度を決定する。全シーン画像の最大幅・最大高を採用し、
 * 長辺が MAX_LONG_EDGE を超えるときは比率を保って縮め、
 * エンコーダ要件のため偶数に切り上げる。画像がなければ既定解像度。
 */
export function resolveCanvasSize(
  imageSizes: { width: number; height: number }[]
): { width: number; height: number } {
  let maxWidth = 0;
  let maxHeight = 0;
  for (const size of imageSizes) {
    maxWidth = Math.max(maxWidth, size.width);
    maxHeight = Math.max(maxHeight, size.height);
  }

  if (maxWidth === 0) maxWidth = VIDEO_GENERATION.DEFAULT_RESOLUTION.WIDTH;
  if (maxHeight === 0) maxHeight = VIDEO_GENERATION.DEFAULT_RESOLUTION.HEIGHT;

  const longEdge = Math.max(maxWidth, maxHeight);
  const scale = longEdge > VIDEO_GENERATION.MAX_LONG_EDGE ? VIDEO_GENERATION.MAX_LONG_EDGE / longEdge : 1;

  return {
    width: toEvenSize(Math.round(maxWidth * scale)),
    height: toEvenSize(Math.round(maxHeight * scale)),
  };
}

/** エンコーダは偶数の幅・高さを要求するため、切り上げて偶数に揃える */
function toEvenSize(value: number): number {
  return Math.ceil(value / 2) * 2;
}

/**
 * フェードトランジションの透明度を計算。
 * 最初のシーンはフェードインせず、最後のシーンはフェードアウトしない。
 */
export function calculateFadeOpacity(
  localTime: number,
  totalDuration: number,
  isFirstScene: boolean,
  isLastScene: boolean
): number {
  const fadeDuration = VIDEO_GENERATION.TRANSITION.FADE_DURATION;

  if (!isFirstScene && localTime < fadeDuration) {
    return localTime / fadeDuration;
  }

  if (!isLastScene && localTime > totalDuration - fadeDuration) {
    return (totalDuration - localTime) / fadeDuration;
  }

  return 1;
}

/**
 * テロップを表示すべき時間帯か。音声が鳴っている区間（beforeの後、afterの前）のみ表示する。
 */
export function isInSubtitleWindow(localTime: number, duration: SceneDuration): boolean {
  return localTime >= duration.beforeTime && localTime < duration.beforeTime + duration.baseTime;
}
